import { create } from "zustand";
import { queryService } from "@/api/services/query-service";
import { useModalStore } from "@/store/use-modal-store";
import {
  CartItem as APICartItem,
  AddToCartRequest,
  CartMerchant,
  CartMerchantItem,
  GlobalCartCalculation,
  CartChange,
} from "@/api/models/api-types";

// 🛒 Legacy Backend Cart Item Interface (Backward compatibility)
export interface CartItem extends APICartItem {
  // Backend'den gelen tüm alanlar + ek local hesaplamalar
  /** Ürün detay sayfası için kullanılacak gerçek product Mongo ID (offer._product) */
  _product?: string;
  priceFormatted: string; // "₺299" format
  totalPrice: number; // price * quantity
}

// 🏪 Cart State Interface (NEW - Hierarchical Structure)
interface CartState {
  // NEW: Hierarchical structure from backend
  merchants: CartMerchant[];
  globalCalculation: GlobalCartCalculation | null;

  // Sepet yüklenirken backend'in otomatik değiştirdiği ürünler
  pendingChanges: CartChange[];

  isLoading: boolean;
  error: string | null;
}

// ⚡ Cart Actions Interface
interface CartActions {
  // 🎯 Core Actions (Backend API)
  addToCart: (request: AddToCartRequest) => Promise<void>;
  removeFromCart: (cartId: string) => Promise<void>;
  updateQuantity: (cartId: string, quantity: number) => Promise<void>;
  updateSelection: (cartItemIds: string[], isSelected: boolean) => Promise<void>;
  clearCart: () => Promise<void>;

  // 🛡️ Utility Actions (Updated for new structure)
  getCartItem: (cartId: string) => CartMerchantItem | undefined;
  getMerchantGroup: (merchantId: string) => CartMerchant | undefined;
  isInCart: (offerId: string) => boolean;

  // 🔄 State Management
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearPendingChanges: () => void;
  reset: () => void;

  // 🌐 Backend API Actions
  loadCart: (offerId?: string) => Promise<void>;
}

type CartStore = CartState & CartActions;
let lastCartStoreChangesKey: string | null = null;

// 🏗️ Initial State
const initialState: CartState = {
  merchants: [],
  globalCalculation: null,
  pendingChanges: [],
  isLoading: false,
  error: null,
};

// 🛒 Backend-Only Cart Store Implementation
export const useCartStore = create<CartStore>((set, get) => ({
  // Initial State
  ...initialState,

  // 🎯 Core Actions (Backend API)
  addToCart: async (request) => {
    try {
      set({ isLoading: true, error: null });

      await queryService.addToCart(request);

      // Cart'ı yeniden yükle
      await get().loadCart();
    } catch (addError: any) {
      console.error("Add to cart error:", addError);
      set({
        error:
          addError?.response?.data?.message ||
          addError?.message ||
          "Ürün sepete eklenirken hata oluştu",
      });
      throw addError;
    } finally {
      set({ isLoading: false });
    }
  },

  removeFromCart: async (cartId) => {
    try {
      set({ isLoading: true, error: null });

      await queryService.deleteFromCart(cartId);

      // Cart'ı yeniden yükle
      await get().loadCart();
    } catch (error) {
      console.error("Remove from cart error:", error);
      set({ error: "Ürün sepetten kaldırılırken hata oluştu" });
    } finally {
      set({ isLoading: false });
    }
  },

  updateQuantity: async (cartId, quantity) => {
    try {
      set({ isLoading: true, error: null });

      if (quantity <= 0) {
        await queryService.deleteFromCart(cartId);
      } else {
        await queryService.updateCartQuantity(cartId, { quantity });
      }

      // Cart'ı yeniden yükle
      await get().loadCart();
    } catch (error) {
      console.error("Update quantity error:", error);
      set({ error: "Ürün miktarı güncellenirken hata oluştu" });
    } finally {
      set({ isLoading: false });
    }
  },

  updateSelection: async (cartItemIds, isSelected) => {
    if (cartItemIds.length === 0) return;
    try {
      set({ error: null });

      await queryService.updateCartSelection({ cartItemIds, isSelected });

      // Cart'ı yeniden yükle - checkbox state'leri backend'den gelir
      await get().loadCart();
    } catch (error) {
      console.error("Update selection error:", error);
      set({ error: "Ürün seçimi güncellenirken hata oluştu" });
    }
  },

  clearCart: async () => {
    try {
      set({ isLoading: true, error: null });

      const { merchants } = get();

      // Tüm merchant'lardaki tüm item'ları sil
      const allItems = merchants.flatMap((m) => m.items);
      await Promise.all(
        allItems.map((item) => queryService.deleteFromCart(item._id)),
      );

      // Cart'ı temizle
      set({ merchants: [], globalCalculation: null });
    } catch (error) {
      console.error("Clear cart error:", error);
      set({ error: "Sepet temizlenirken hata oluştu" });
    } finally {
      set({ isLoading: false });
    }
  },

  // 🛡️ Utility Actions (Updated for hierarchical structure)
  getCartItem: (cartId) => {
    const { merchants } = get();
    for (const merchant of merchants) {
      const item = merchant.items.find((item) => item._id === cartId);
      if (item) return item;
    }
    return undefined;
  },

  getMerchantGroup: (merchantId) => {
    return get().merchants.find((m) => m.merchantId === merchantId);
  },

  isInCart: (offerId) => {
    const { merchants } = get();
    return merchants.some((merchant) =>
      merchant.items.some((item) => item.offer.offerId === offerId),
    );
  },

  // 🔄 State Management
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  clearPendingChanges: () => set({ pendingChanges: [] }),
  reset: () => set({ ...initialState }),

  // 🌐 Backend API Actions (Updated - Hierarchical Structure)
  loadCart: async (offerId?: string) => {
    try {
      set({ isLoading: true, error: null });

      // Backend'den hierarchical cart data çek
      // offerId varsa sadece o teklife ait ürünü döner (hemen al akışı)
      const response = await queryService.getAllCart(offerId);

      // 🛡️ Defensive: API response validation
      if (!response?.data?.merchants) {
        console.warn("Load cart: Invalid API response structure", response);
        set({ merchants: [], globalCalculation: null });
        return;
      }

      // Hierarchical data'yı direkt store'a kaydet (backend calculation'larıyla)
      // Backend'in otomatik değiştirdiği ürünler varsa pendingChanges'e kaydet
      set({
        merchants: response.data.merchants,
        globalCalculation: response.data.calculation,
        ...(response.data.changes?.length
          ? { pendingChanges: response.data.changes }
          : {}),
      });

      if (response.data.changes?.length) {
        const changesKey = JSON.stringify(response.data.changes);
        if (lastCartStoreChangesKey !== changesKey) {
          lastCartStoreChangesKey = changesKey;
          useModalStore.getState().openCartChanges(response.data.changes);
        }
      } else {
        lastCartStoreChangesKey = null;
      }
    } catch (cartError: any) {
      console.error("Load cart error:", cartError);
      set({
        error: "Sepet yüklenirken hata oluştu",
        merchants: [],
        globalCalculation: null,
      });
    } finally {
      set({ isLoading: false });
    }
  },
}));
